import React, { useState } from "react";
import { ArrowUpLeft } from "lucide-react";
import type { Project } from "../../data/projects";

/**
 * Some of the work is behind a login or boots into a splash screen, so a live
 * capture would show a spinner rather than the product. Those get a
 * typographic plate instead: on-system, and honest about what it is.
 */
const Plate: React.FC<{ project: Project }> = ({ project }) => (
  <div className="flex h-full w-full flex-col justify-between border border-[var(--line)] bg-surface p-6">
    <span className="t-meta">{project.tags[0]}</span>
    <span className="t-h2 pe-4 text-fg-2">{project.title}</span>
  </div>
);

export const ProjectCard: React.FC<{
  project: Project;
  ratio?: string;
  size?: "lg" | "md";
}> = ({ project, ratio = "16 / 10", size = "md" }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const internal = project.link.startsWith("/");
  const hasLink = project.link !== "";
  const showPlate = !project.thumbnail || imageFailed;

  const Wrapper: React.ElementType = hasLink ? "a" : "div";
  const linkProps = hasLink
    ? {
        href: project.link,
        ...(internal ? {} : { target: "_blank", rel: "noopener noreferrer" }),
      }
    : {};

  return (
    <Wrapper {...linkProps} className="group block">
      <div className="relative overflow-hidden bg-surface" style={{ aspectRatio: ratio }}>
        {showPlate ? (
          <Plate project={project} />
        ) : (
          <>
            <img
              src={project.thumbnail}
              alt={`צילום מסך של ${project.title}`}
              loading="lazy"
              className="h-full w-full object-cover object-top transition-transform duration-[900ms] ease-out-expo group-hover:scale-[1.035]"
              onError={() => setImageFailed(true)}
            />

            {/* A low ink wash keeps every screenshot inside the palette so the
                grid reads as one piece instead of a clash of client brands. */}
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/15 to-transparent transition-opacity duration-500 group-hover:opacity-60"
            />
          </>
        )}

        {hasLink && (
          <span
            aria-hidden="true"
            className="absolute left-3 top-3 flex h-9 w-9 translate-y-2 items-center justify-center border border-signal/40 bg-ink/75 text-signal opacity-0 backdrop-blur-sm transition-all duration-500 ease-out-expo group-hover:translate-y-0 group-hover:opacity-100"
          >
            <ArrowUpLeft className="h-4 w-4" />
          </span>
        )}
      </div>

      <div className="pt-5">
        <div className="mb-2 flex flex-wrap gap-x-3 gap-y-1">
          {project.tags.map((tag) => (
            <span key={tag} className="t-data text-[0.65rem] tracking-widest text-fg-3">
              {tag}
            </span>
          ))}
        </div>

        <h3
          className={`${size === "lg" ? "t-h2" : "t-h3"} text-fg transition-colors duration-300 group-hover:text-signal`}
        >
          {project.title}
        </h3>

        <p className="t-body mt-3 text-sm">{project.description}</p>

        {hasLink && !internal && (
          <span className="t-data mt-3 inline-block text-[0.7rem] text-fg-3" dir="ltr">
            {project.link.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}
          </span>
        )}
      </div>
    </Wrapper>
  );
};

export default ProjectCard;
